/**
 * Admin Hub Command Palette Component
 * Cmd+K / Ctrl+K powered search and navigation
 */
import React, { useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  InputBase,
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Chip,
  alpha,
} from '@mui/material';
import {
  Search as SearchIcon,
  History as RecentIcon,
  Star as FavoriteIcon,
  NavigateNext as NavIcon,
} from '@mui/icons-material';
import { useAdminHubCommandPalette } from '../../hooks/useAdminHubCommandPalette';
import { ADMIN_HUB_DESIGN } from '../../config/adminHubConfig';

// Search result type
type ResultType = 'navigation' | 'recent' | 'favorite' | 'action';

interface SearchResultItem {
  id: string;
  label: string;
  path: string;
  section: string;
  score: number;
  type: ResultType;
}

// Group search results by type
interface GroupedResults {
  recent: SearchResultItem[];
  favorite: SearchResultItem[];
  navigation: SearchResultItem[];
}

export const AdminHubCommandPalette: React.FC = () => {
  const {
    isOpen,
    close,
    searchQuery,
    setSearchQuery,
    searchResults,
    selectedIndex,
    setSelectedIndex,
    selectNext,
    selectPrevious,
    executeSelected,
    executeResult,
  } = useAdminHubCommandPalette();

  const inputRef = useRef<HTMLInputElement>(null);

  // Focus input when dialog opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd+K or Ctrl+K to open
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        if (!isOpen) {
          // This will be handled by the parent context
          // The hook just manages state
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // Handle dialog keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        selectNext();
        break;
      case 'ArrowUp':
        e.preventDefault();
        selectPrevious();
        break;
      case 'Enter':
        e.preventDefault();
        executeSelected();
        break;
      case 'Escape':
        e.preventDefault();
        close();
        break;
    }
  }, [selectNext, selectPrevious, executeSelected, close]);

  // Group results by type
  const groupedResults: GroupedResults = {
    recent: searchResults.filter(r => r.type === 'recent') as SearchResultItem[],
    favorite: searchResults.filter(r => r.type === 'favorite') as SearchResultItem[],
    navigation: searchResults.filter(r => r.type === 'navigation') as SearchResultItem[],
  };

  // Calculate global index for selection
  let globalIndex = 0;
  const getResultWithGlobalIndex = (result: SearchResultItem) => {
    const index = globalIndex;
    globalIndex++;
    return { ...result, globalIndex: index };
  };

  // Keyboard shortcut hint
  const isMac = typeof navigator !== 'undefined' && navigator.platform.toUpperCase().indexOf('MAC') >= 0;

  return (
    <Dialog
      open={isOpen}
      onClose={close}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          position: 'absolute',
          top: '15%',
          mx: 2,
          maxHeight: '70vh',
          borderRadius: 2,
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        {/* Search input */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 2,
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <SearchIcon sx={{ color: 'text.secondary', mr: 1.5 }} />
          <InputBase
            ref={inputRef}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search admin pages..."
            fullWidth
            sx={{
              fontSize: '1rem',
            }}
          />
          <Chip
            label={isMac ? 'esc' : 'Esc'}
            size="small"
            sx={{
              ml: 1,
              height: 24,
              fontSize: '0.75rem',
              backgroundColor: 'action.hover',
            }}
          />
        </Box>

        {/* Results */}
        <Box sx={{ maxHeight: '50vh', overflow: 'auto' }}>
          {searchResults.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary" variant="body2">
                {searchQuery ? 'No results found' : 'Start typing to search...'}
              </Typography>
            </Box>
          ) : (
            <List sx={{ py: 1 }}>
              {/* Recent items */}
              {groupedResults.recent.length > 0 && (
                <>
                  <Box sx={{ px: 2, py: 0.5 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                      }}
                    >
                      Recent
                    </Typography>
                  </Box>
                  {groupedResults.recent.map(result => {
                    const indexed = getResultWithGlobalIndex(result);
                    return (
                      <ResultItem
                        key={result.id}
                        result={result}
                        isSelected={selectedIndex === indexed.globalIndex}
                        icon={<RecentIcon />}
                        onSelect={() => executeResult(result)}
                        onHover={() => setSelectedIndex(indexed.globalIndex)}
                      />
                    );
                  })}
                </>
              )}

              {/* Favorites */}
              {groupedResults.favorite.length > 0 && (
                <>
                  <Box sx={{ px: 2, py: 0.5, mt: groupedResults.recent.length > 0 ? 1 : 0 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                      }}
                    >
                      Favorites
                    </Typography>
                  </Box>
                  {groupedResults.favorite.map(result => {
                    const indexed = getResultWithGlobalIndex(result);
                    return (
                      <ResultItem
                        key={result.id}
                        result={result}
                        isSelected={selectedIndex === indexed.globalIndex}
                        icon={<FavoriteIcon sx={{ color: 'warning.main' }} />}
                        onSelect={() => executeResult(result)}
                        onHover={() => setSelectedIndex(indexed.globalIndex)}
                      />
                    );
                  })}
                </>
              )}

              {/* Navigation results */}
              {groupedResults.navigation.length > 0 && (
                <>
                  <Box sx={{ px: 2, py: 0.5, mt: (groupedResults.recent.length > 0 || groupedResults.favorite.length > 0) ? 1 : 0 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                      }}
                    >
                      Navigation
                    </Typography>
                  </Box>
                  {groupedResults.navigation.map(result => {
                    const indexed = getResultWithGlobalIndex(result);
                    return (
                      <ResultItem
                        key={result.id}
                        result={result}
                        isSelected={selectedIndex === indexed.globalIndex}
                        icon={<NavIcon />}
                        onSelect={() => executeResult(result)}
                        onHover={() => setSelectedIndex(indexed.globalIndex)}
                      />
                    );
                  })}
                </>
              )}
            </List>
          )}
        </Box>

        {/* Footer with keyboard hints */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            px: 2,
            py: 1,
            borderTop: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'action.hover',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Chip label="↑↓" size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
            <Typography variant="caption" color="text.secondary">navigate</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Chip label="↵" size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
            <Typography variant="caption" color="text.secondary">select</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Chip label="esc" size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
            <Typography variant="caption" color="text.secondary">close</Typography>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

// Individual result item component
interface ResultItemProps {
  result: { id: string; label: string; path: string; section: string };
  isSelected: boolean;
  icon: React.ReactNode;
  onSelect: () => void;
  onHover: () => void;
}

const ResultItem: React.FC<ResultItemProps> = ({
  result,
  isSelected,
  icon,
  onSelect,
  onHover,
}) => {
  return (
    <ListItem disablePadding>
      <ListItemButton
        selected={isSelected}
        onClick={onSelect}
        onMouseEnter={onHover}
        sx={{
          py: 1,
          px: 2,
          '&.Mui-selected': {
            backgroundColor: alpha(ADMIN_HUB_DESIGN.COLORS.ACTIVE_ITEM, 0.1),
            '&:hover': {
              backgroundColor: alpha(ADMIN_HUB_DESIGN.COLORS.ACTIVE_ITEM, 0.15),
            },
          },
        }}
      >
        <ListItemIcon sx={{ minWidth: 36 }}>
          {icon}
        </ListItemIcon>
        <ListItemText
          primary={result.label}
          secondary={result.section}
          primaryTypographyProps={{
            variant: 'body2',
            fontWeight: isSelected ? 500 : 400,
          }}
          secondaryTypographyProps={{
            variant: 'caption',
          }}
        />
      </ListItemButton>
    </ListItem>
  );
};

export default AdminHubCommandPalette;
